import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AsyncBoundary, Card, Drawer,
} from '@kollez/design-system';
import {
  useFacultyDirectory, useFacultyDetail,
  type FacultyPhdStatus, type FacultyType, type FacultyStatus, type FacultyRow,
} from '@/lib/queries';

/**
 * /faculty (Page 67/N) — Faculty directory scoped by tenant + department.
 *
 * FacultyProfile has real seeded data (dummy-faculty-roster.ts) and the
 * /faculty/directory + /faculty/directory/:id API surfaces from Pass 15b.
 * No page consumed it — HoDs / principals / TPOs had no way to see the
 * faculty at their college outside of individual mentee views. Now
 * surfaces the collection as a directory the role can filter and drill
 * into.
 *
 * Roles that see this: HoD (dept-scoped by server via tenantScope),
 * principal / TPO (college-scoped), platform_admin (all).
 */

const PHD_LABEL: Record<FacultyPhdStatus, string> = {
  holds_phd: 'Holds PhD',
  pursuing_phd: 'Pursuing PhD',
  not_pursuing: 'Not pursuing',
};

const TYPE_LABEL: Record<FacultyType, string> = {
  permanent: 'Permanent',
  adjunct: 'Adjunct',
  visiting: 'Visiting',
  guest: 'Guest',
  contract: 'Contract',
};

const STATUS_LABEL: Record<FacultyStatus, string> = {
  active: 'Active',
  on_leave: 'On leave',
  sabbatical: 'Sabbatical',
  retired: 'Retired',
  terminated: 'Terminated',
};

export function HodFacultyPage() {
  const [phdStatus, setPhdStatus] = useState<FacultyPhdStatus | ''>('');
  const [facultyType, setFacultyType] = useState<FacultyType | ''>('');
  const [status, setStatus] = useState<FacultyStatus | ''>('');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const list = useFacultyDirectory({ phdStatus, facultyType, status, q, sortBy: 'fullName', sortDir: 'asc', limit: 100 });
  const detail = useFacultyDetail(openId ?? undefined);

  const rows = list.data?.rows ?? [];
  const summary = list.data?.summary;

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">People</p>
          <h1 className="h-page">Faculty</h1>
          <p className="subhead">
            Everyone teaching at your college. Filter by PhD status, type, and status —
            the numbers here feed NAAC/NBA/NIRF submissions.
          </p>
        </div>
      </div>

      {summary && (
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <div className="card kpi">
            <span className="kpi-label">Holds PhD</span>
            <span className="kpi-value">{summary.byPhdStatus.holds_phd ?? 0}</span>
          </div>
          <div className="card kpi">
            <span className="kpi-label">Pursuing PhD</span>
            <span className="kpi-value">{summary.byPhdStatus.pursuing_phd ?? 0}</span>
          </div>
          <div className="card kpi">
            <span className="kpi-label">Permanent</span>
            <span className="kpi-value">{summary.byFacultyType.permanent ?? 0}</span>
          </div>
          <div className="card kpi">
            <span className="kpi-label">Adjunct / visiting</span>
            <span className="kpi-value">{(summary.byFacultyType.adjunct ?? 0) + (summary.byFacultyType.visiting ?? 0)}</span>
          </div>
        </div>
      )}

      <div className="card">
        <div className="filterbar">
          <input className="input" placeholder="Search by name or employee ID" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="select" value={phdStatus} onChange={(e) => setPhdStatus(e.target.value as FacultyPhdStatus | '')}>
            <option value="">Any PhD status</option>
            {(Object.entries(PHD_LABEL) as Array<[FacultyPhdStatus, string]>).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select className="select" value={facultyType} onChange={(e) => setFacultyType(e.target.value as FacultyType | '')}>
            <option value="">Any type</option>
            {(Object.entries(TYPE_LABEL) as Array<[FacultyType, string]>).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value as FacultyStatus | '')}>
            <option value="">Any status</option>
            {(Object.entries(STATUS_LABEL) as Array<[FacultyStatus, string]>).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <span className="fb-count">{list.data?.meta?.total ?? rows.length} listed</span>
        </div>

        <AsyncBoundary
          kind="table"
          inCard
          loading={list.isLoading}
          error={list.error}
          empty={!list.isLoading && rows.length === 0}
          emptyTitle="No faculty listed"
          emptyHint="Ask your registrar to import the faculty roster."
          onRetry={() => void list.refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Name</th>
                  <th className="hide-mobile">Employee ID</th>
                  <th className="hide-mobile">Department</th>
                  <th>Type</th>
                  <th className="hide-mobile">PhD</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r: FacultyRow) => (
                  <tr key={r._id} onClick={() => setOpenId(r._id)} style={{ cursor: 'pointer' }}>
                    <td className="cell-title">{r.fullName}</td>
                    <td className="hide-mobile cell-sub">{r.employeeId ?? '—'}</td>
                    <td className="hide-mobile cell-sub">
                      {typeof r.department === 'object' ? r.department.name : '—'}
                    </td>
                    <td className="cell-sub">{TYPE_LABEL[r.facultyType]}</td>
                    <td className="hide-mobile cell-sub">{PHD_LABEL[r.phdStatus]}</td>
                    <td>
                      <span className={`badge ${r.status === 'active' ? 'badge-success' : r.status === 'terminated' ? 'badge-danger' : ''}`}>
                        {STATUS_LABEL[r.status]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </div>

      <Drawer open={Boolean(openId)} onClose={() => setOpenId(null)} title={detail.data?.profile?.fullName ?? ''} size="lg">
        {detail.isLoading && <div className="p-4 cell-sub">Loading…</div>}
        {detail.data?.profile && (
          <div className="stack">
            <div>
              <div className="flex items-baseline gap-2 flex-wrap">
                <h2 className="h-section m-0">{detail.data.profile.fullName}</h2>
                {detail.data.profile.employeeId && <span className="cell-sub">· {detail.data.profile.employeeId}</span>}
              </div>
              <p className="cell-sub m-0 mt-1">
                {typeof detail.data.profile.department === 'object' ? detail.data.profile.department?.name : '—'}
                {' · '}
                {TYPE_LABEL[detail.data.profile.facultyType]}
                {' · '}
                {STATUS_LABEL[detail.data.profile.status]}
              </p>
            </div>

            {detail.data.profile.bio && (
              <Card padding="lg">
                <p className="m-0 whitespace-pre-line">{detail.data.profile.bio}</p>
              </Card>
            )}

            <div className="grid gap-3 md:grid-cols-2">
              <Card padding="md">
                <h3 className="h-subsection mb-2">Academic</h3>
                <dl className="dl">
                  <dt>PhD status</dt><dd>{PHD_LABEL[detail.data.profile.phdStatus]}</dd>
                  {detail.data.profile.hIndex !== undefined && (
                    <><dt>h-index</dt><dd>{detail.data.profile.hIndex}</dd></>
                  )}
                  {detail.data.profile.publications !== undefined && (
                    <><dt>Publications</dt><dd>{detail.data.profile.publications}</dd></>
                  )}
                  {detail.data.profile.patents !== undefined && (
                    <><dt>Patents</dt><dd>{detail.data.profile.patents}</dd></>
                  )}
                  {detail.data.profile.yearsOfExperience !== undefined && (
                    <><dt>Experience</dt><dd>{detail.data.profile.yearsOfExperience} years</dd></>
                  )}
                </dl>
              </Card>

              {detail.data.profile.subjectsTaught && detail.data.profile.subjectsTaught.length > 0 && (
                <Card padding="md">
                  <h3 className="h-subsection mb-2">Subjects taught</h3>
                  <div className="flex flex-wrap gap-1">
                    {detail.data.profile.subjectsTaught.map((s) => (
                      <span key={s} className="qtype">{s}</span>
                    ))}
                  </div>
                </Card>
              )}
            </div>

            {detail.data.profile.researchAreas && detail.data.profile.researchAreas.length > 0 && (
              <Card padding="md">
                <h3 className="h-subsection mb-2">Research areas</h3>
                <div className="flex flex-wrap gap-1">
                  {detail.data.profile.researchAreas.map((s) => (
                    <span key={s} className="qtype">{s}</span>
                  ))}
                </div>
              </Card>
            )}

            {detail.data.profile.qualifications && detail.data.profile.qualifications.length > 0 && (
              <Card padding="md">
                <h3 className="h-subsection mb-2">Qualifications</h3>
                <ul className="grid gap-1 text-sm">
                  {detail.data.profile.qualifications.map((q, i) => (
                    <li key={i}>
                      <b>{q.degree}</b> · {q.institution}{q.year ? ` · ${q.year}` : ''}
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {typeof detail.data.profile.user === 'object' && detail.data.profile.user?.email && (
              <p className="cell-sub">
                <Link className="link" to={`mailto:${detail.data.profile.user.email}`}>
                  {detail.data.profile.user.email}
                </Link>
              </p>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
